import type {Dispatch} from 'react';

import {applyPatch, compare} from 'fast-json-patch';
import type * as z from 'zod';

import {
  updateLessonTool,
  updateLessonToolParameters,
} from '@edume/bento/lessonChatbot';
import type {patchOperation} from '@edume/bento/lessonChatbot';
import {
  editorSlidesArraySchema,
  isIssueCodeNonMandatory,
} from '@edume/bento/lessonValidation';

import type {ChatContext, ChatTool} from '../../Chat/types';
import {actionTypes} from '../SlideshowCanvas/reducerState';
import {isKeyExcludedFromDuplication} from '../SlideshowCanvas/stateUtils';
import {sanitizeAssistantContent} from './assistantUtils';

export type LLMTool = ChatTool & {
  handler: (params: unknown, callId: string) => void;
  validate?: (arg: {callId: string; args: unknown}) => void;
};

export type ToolContext = {
  dispatch: Dispatch<any>;
  updateToolCall: ChatContext['updateToolCall'];
  getState: () => {slides: Record<string, unknown>[]};
};

const getPathSegments = (path: string) =>
  path
    .split('/')
    .slice(1)
    .map((s) => s.replace(/~1/g, '/').replace(/~0/g, '~'));

const isRestrictedEditPath = (path: string) => {
  if (!path) {
    return false;
  }
  const pathSegments = getPathSegments(path);

  const lastSegment = pathSegments[pathSegments.length - 1];

  return (
    isKeyExcludedFromDuplication(lastSegment) ||
    pathSegments.some((s) => ['image', 'video'].includes(s))
  );
};

const hasNonRestrictedPatchOperationPaths = (
  operation: z.infer<typeof patchOperation>
) =>
  !isRestrictedEditPath(operation.path) &&
  !('from' in operation && isRestrictedEditPath(operation.from));

const isPathToCkeyArrayElement = (path: string) => {
  const pathSegments = getPathSegments(path);
  if (pathSegments.length < 2) {
    return false;
  }

  const lastSegment = pathSegments[pathSegments.length - 1];
  const lastSegmentIsAnIndex = !isNaN(Number(lastSegment));
  const secondLastSegmentIsCkey =
    pathSegments[pathSegments.length - 2].endsWith('Ckey');

  return lastSegmentIsAnIndex && secondLastSegmentIsCkey;
};

export const createAITools = (context: ToolContext): LLMTool[] => [
  {
    ...(updateLessonTool satisfies ChatTool),
    handler: (rawParams: unknown, callId: string) => {
      const clone = structuredClone(context.getState().slides);
      try {
        const paramsRes = updateLessonToolParameters.safeParse(rawParams);
        if (!paramsRes.success) {
          context.updateToolCall({
            callId,
            result: `Error: ${JSON.stringify(paramsRes.error.issues)}`,
            status: {type: 'incomplete'},
          });
          return;
        }

        const {patch: unprocessedPatch} = paramsRes.data;
        const sanitizedParams = unprocessedPatch
          .filter(hasNonRestrictedPatchOperationPaths)
          .map((p) => {
            if (p.op !== 'add' && p.op !== 'replace') {
              return p;
            }

            if (isPathToCkeyArrayElement(p.path)) {
              if (p.value) {
                return {
                  ...p,
                  value: '',
                };
              }
              return p;
            }

            return {
              ...p,
              value: sanitizeAssistantContent(p).value,
            };
          });

        const {newDocument: updatedSlides} = applyPatch(
          clone,
          sanitizedParams,
          true,
          false
        );

        const incompleteEditorSlidesArraySchema = editorSlidesArraySchema.check(
          (ctx) => {
            ctx.issues = ctx.issues.filter((i) => !isIssueCodeNonMandatory(i));
          }
        );
        const res = incompleteEditorSlidesArraySchema.safeParse(updatedSlides);

        const diff = compare(unprocessedPatch, sanitizedParams);

        const getDiffErrorMessage = () =>
          `The patch contained either identifier keys which are generated server side, invalid emoji codes or url and/or image fields which are only editable through the UI by the user and had to be removed. Therefore the following change was made to the patch before it could be applied: ${JSON.stringify(diff)}.`;

        if (!res.success) {
          const errors =
            diff.length > 0
              ? [getDiffErrorMessage(), ...res.error.issues]
              : res.error.issues;
          context.updateToolCall({
            callId,
            result: `Error: ${JSON.stringify(errors)}`,
            status: {type: 'incomplete'},
          });
          return;
        }

        context.dispatch({
          type: actionTypes.REPLACE_SLIDES,
          payload: updatedSlides,
        });

        context.updateToolCall({
          callId,
          result:
            diff.length > 0
              ? `Error: ${getDiffErrorMessage()}`
              : 'Applied patch',
          status: {type: 'completed'},
        });
      } catch (error) {
        console.log('Error applying patch:', error);
        context.updateToolCall({
          callId,
          result: `Error applying patch: ${error instanceof Error ? error.message : 'Unknown error'}`,
          status: {type: 'incomplete'},
        });
      }
    },
  },
];

export const serializeToolsForAPI = (tools: LLMTool[]) =>
  tools.map(({handler, validate, requiresUserInvocation, ...tool}) => tool);
